import { useMemo, useState } from "react"
import { Badge, Box, Flex, Heading, Link, Menu, Text } from "@chakra-ui/react"
import { Chart, useChart } from "@chakra-ui/charts"
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import type { Graph } from "@hydrologiq/schemas/1.0.0/installationPage"
import { IANATimeZone } from "@hydrologiq/common/helpers/date"
import type { DateTime } from "timezonecomplete"
import {
  appendTimeMs,
  niceYAxisMax,
  timescaleChartMarginRight,
  timescaleMinTickGap,
  timescaleSpanMs,
  timescaleTickFormatter,
  timescaleTimeDomain,
  type TimeSeriesDataPoint,
  type TimeSeriesSeries,
} from "./TimeSeries"

const TIMESCALE_MONITORING_CHART_HEIGHT = "400px"

export interface GroupedBarBreakdownChartProps {
  /** Wide-table rows keyed by series name (+ `timestamp`). */
  data: TimeSeriesDataPoint[]
  /** One entry per category series (e.g. Diesel / Water / Waste). */
  series: TimeSeriesSeries<string[]>[]
  graph: Graph
  /** Unit suffix shown in the tooltip (e.g. "%"). */
  unit?: string
  chartHeight?: string
  /** Shared UTC-ms tick positions so the X axis aligns with other cards. */
  xAxisTicksMs?: number[]
  /** Tick density hint, used for the label gap when no tick positions are given. */
  xAxisTickInterval?: number
  chartAxisStartDate?: DateTime
  chartAxisEndDate?: DateTime
  /**
   * Show an asset-selection dropdown to toggle which category series render.
   * Used on the hybrid parent view (per-asset tank breakdown); omitted on the
   * system view where categories aggregate across hybrids.
   */
  showAssetMenu?: boolean
}

const formatValueWithDecimals = (value: number): string => {
  if (value === 0 || Number.isInteger(value)) {
    return value.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
  }
  if (Math.abs(value) < 1) return value.toFixed(2)
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })
}

/**
 * Expected tank-category series names, as set by the backend. This union is the
 * single source of truth for the semantic-colour coupling below: the colour
 * override matches on these names (case-insensitive), and STORAGE_CATEGORY_COLORS
 * is keyed by exactly these categories. If the backend ever relabels these
 * series (e.g. localised, or "Diesel Tank"), the match falls back to the
 * rotating palette — update this type and the map together when that contract
 * changes.
 */
type StorageCategory = "diesel" | "water" | "waste"

/**
 * Semantic colours per tank category, keyed by the series name (which the
 * backend sets to the category). This makes the grouped tank-level bars read
 * intuitively — brown diesel, blue water, grey waste — instead of the generic
 * rotating palette. Case-insensitive match; any series whose name isn't a
 * known category keeps its palette colour.
 */
const STORAGE_CATEGORY_COLORS: Record<StorageCategory, string> = {
  diesel: "#8B5A2B", // brown (fuel)
  water: "#2563eb", // blue-600
  waste: "#6b7280", // gray-500
}

interface GroupedBarTooltipProps {
  active?: boolean
  // Recharts passes an untyped payload array; each entry carries dataKey/value.
  payload?: Array<{ dataKey?: string; value?: number; name?: string }>
  label?: number
  series: TimeSeriesSeries<string[]>[]
  unit?: string
}

const GroupedBarTooltip = ({ active, payload, label, series, unit }: GroupedBarTooltipProps) => {
  if (!active || !payload || payload.length === 0) return null

  const tz = IANATimeZone()
  const timeLabel =
    typeof label === "number"
      ? new Intl.DateTimeFormat(undefined, {
          timeZone: tz,
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }).format(new Date(label))
      : ""

  const suffix = unit ? ` ${unit}` : ""

  return (
    <Box bg="white" borderRadius="6px" boxShadow="md" p={3} minW="160px">
      {timeLabel && (
        <Text fontSize="sm" fontWeight="medium" mb={2} color="fg.muted">
          {timeLabel}
        </Text>
      )}
      <Flex direction="column" gap={1}>
        {payload
          .filter((entry) => typeof entry.value === "number" && Number.isFinite(entry.value))
          .map((entry) => {
            const cfg = series.find((s) => s.name === entry.dataKey)
            const displayLabel = cfg?.label ?? String(entry.dataKey ?? entry.name ?? "")
            return (
              <Flex key={String(entry.dataKey)} justify="space-between" gap={4}>
                <Flex align="center" gap={2}>
                  <Box w="10px" h="10px" borderRadius="2px" bg={cfg?.color} />
                  <Text fontSize="sm">{displayLabel}</Text>
                </Flex>
                <Text fontSize="sm" fontWeight="medium">
                  {formatValueWithDecimals(entry.value as number)}
                  {suffix}
                </Text>
              </Flex>
            )
          })}
      </Flex>
    </Box>
  )
}

/**
 * Horizontal legend matching the shared monitoring legend style (12 px colour
 * swatch + `gray.700` label at `fontSize="sm"`, centred), so this card reads
 * consistently with the stacked-bar / line charts rather than the recharts
 * default legend.
 */
const GroupedBarLegend = ({ series }: { series: TimeSeriesSeries<string[]>[] }) => {
  if (!series.length) return null
  return (
    <Flex direction="row" gap={4} justifyContent="center" alignItems="center" mb={2} flexWrap="wrap">
      {series.map((s) => (
        <Flex key={String(s.name)} data-legend-item alignItems="center" gap={1} fontSize="sm">
          <Box width="12px" height="12px" backgroundColor={s.color} borderRadius="sm" />
          <Text color="gray.700">{s.label ?? String(s.name)}</Text>
        </Flex>
      ))}
    </Flex>
  )
}

/**
 * Grouped (side-by-side) bar chart for a per-category breakdown.
 *
 * Unlike ``StackedBarBreakdownChart`` (which sums segments into a single stack
 * via a shared ``stackId``), this renders one ``<Bar>`` per series with NO
 * shared stackId, so Recharts places the category bars next to each other in
 * each time bucket — a grouped category chart. Purpose-built for the hybrid
 * tank-level card (Diesel / Water / Waste level %), keeping the stacked
 * component untouched.
 */
export default function GroupedBarBreakdownChart({
  data,
  series,
  graph,
  unit,
  chartHeight = TIMESCALE_MONITORING_CHART_HEIGHT,
  xAxisTicksMs,
  xAxisTickInterval,
  chartAxisStartDate,
  chartAxisEndDate,
  showAssetMenu = false,
}: GroupedBarBreakdownChartProps) {
  // Only real category series (drop any peak/remainder helpers that a caller
  // might pass through — this card has none, but stay defensive). Override the
  // colour with the semantic per-category colour when the series name is a
  // known storage category.
  const allBarSeries = useMemo(
    () =>
      series
        .filter(
          (s) =>
            !s.name.startsWith("_") && !s.name.endsWith("_peak") && !s.name.startsWith("overlay_")
        )
        .map((s) => {
          const key = String(s.name).toLowerCase() as StorageCategory
          const semantic = STORAGE_CATEGORY_COLORS[key]
          return semantic ? { ...s, color: semantic } : s
        }),
    [series]
  )

  // Asset-selection state: which series names are visible. Defaults to all.
  // Only surfaced when showAssetMenu is set (hybrid parent view). A guard keeps
  // at least one series selected so the chart never goes blank.
  const [deselected, setDeselected] = useState<Set<string>>(new Set())
  const barSeries = useMemo(
    () =>
      showAssetMenu ? allBarSeries.filter((s) => !deselected.has(String(s.name))) : allBarSeries,
    [allBarSeries, deselected, showAssetMenu]
  )

  const toggleSeries = (name: string) => {
    setDeselected((prev) => {
      const next = new Set(prev)
      if (next.has(name)) {
        next.delete(name)
      } else {
        // Keep at least one series selected.
        const remaining = allBarSeries.length - next.size
        if (remaining <= 1) return prev
        next.add(name)
      }
      return next
    })
  }

  // "Select all" toggle: if everything is already selected, collapse to the
  // first series only (mirrors the line-chart selector's deselect-to-one); else
  // clear all deselections so every series shows.
  const selectAll = () => {
    setDeselected((prev) => {
      if (prev.size === 0) {
        const first = allBarSeries[0]?.name
        return first ? new Set(allBarSeries.slice(1).map((s) => String(s.name))) : prev
      }
      return new Set()
    })
  }

  // Same buckets as the other graphs on the page, so bars line up with them over
  // the same period. Memoized so unrelated re-renders don't rebuild the rows.
  const dataForChart = useMemo(() => appendTimeMs(data), [data])

  const chart = useChart({
    data: dataForChart,
    series: barSeries,
  } as unknown as Parameters<typeof useChart>[0])

  // The same x-axis rules as every other graph (see TimeSeries.tsx)
  const timeDomain = timescaleTimeDomain(xAxisTicksMs, chartAxisStartDate, chartAxisEndDate)
  const formatTimeTick = useMemo(
    () => timescaleTickFormatter(timescaleSpanMs(chartAxisStartDate, chartAxisEndDate)),
    [chartAxisStartDate, chartAxisEndDate]
  )
  const timeMinTickGap = timescaleMinTickGap(xAxisTickInterval, dataForChart)

  const formatYAxisTick = (value: number) => formatValueWithDecimals(value)

  const selectedCount = allBarSeries.length - deselected.size

  return (
    <Box position="relative" width="100%" minW={0}>
      <Flex alignItems="center" justifyContent="space-between" mb={2} gap={3}>
        {graph?.title ? (
          <Heading as="h3" size="sm">
            {graph.title}
          </Heading>
        ) : (
          <Box />
        )}
        {/* Asset selection dropdown — mirrors the shared line-chart selector
            (trigger styling, "Select all" link, Badge rows that grey out when
            deselected) so it reads identically to the rest of monitoring. */}
        {showAssetMenu && allBarSeries.length > 0 && (
          <Menu.Root closeOnSelect={false}>
            <Menu.Trigger asChild>
              <Box
                as="button"
                padding={2}
                borderWidth="1px"
                borderRadius="md"
                borderColor="border"
                backgroundColor="white"
                cursor="pointer"
                width={{ base: "200px", md: "250px" }}
                textAlign="left"
                fontSize="sm"
                _hover={{ borderColor: "blue.300" }}
              >
                <Flex alignItems="center" justifyContent="space-between">
                  <Text truncate>
                    {selectedCount === allBarSeries.length
                      ? `All Assets (${selectedCount})`
                      : `${selectedCount}/${allBarSeries.length} assets`}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    ▼
                  </Text>
                </Flex>
              </Box>
            </Menu.Trigger>
            <Menu.Positioner>
              <Menu.Content width="400px" maxH="500px" overflowY="auto">
                <Box borderBottomWidth="2px" borderColor="gray.200" pb={2} mb={3} mx={2}>
                  <Flex alignItems="center" justifyContent="space-between" gap={4} fontSize="sm">
                    <Link variant="primary" onClick={selectAll} cursor="pointer">
                      Select all
                    </Link>
                  </Flex>
                </Box>
                {allBarSeries.map((s) => {
                  const name = String(s.name)
                  const isSelected = !deselected.has(name)
                  const isLastSelected = isSelected && selectedCount === 1
                  return (
                    <Box key={name} mb={1} mx={2}>
                      <Menu.Item
                        value={name}
                        onSelect={() => toggleSeries(name)}
                        cursor="pointer"
                        _hover={{ backgroundColor: "gray.50" }}
                        borderRadius="sm"
                        disabled={isLastSelected}
                      >
                        <Flex alignItems="center" gap={2}>
                          {/* Presentational only — the real toggle lives on
                              Menu.Item onSelect (keyboard-accessible via the
                              menu item). readOnly keeps this a controlled input
                              without an empty onChange / React warning. */}
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={isLastSelected}
                            readOnly
                          />
                          <Badge
                            backgroundColor={s.color}
                            color="white"
                            fontSize="xs"
                            minW="12px"
                            textAlign="center"
                            opacity={isSelected ? 1 : 0.5}
                          >
                            ●
                          </Badge>
                          <Text fontSize="sm" color={isSelected ? "black" : "gray.500"}>
                            {s.label ?? name}
                          </Text>
                        </Flex>
                      </Menu.Item>
                    </Box>
                  )
                })}
              </Menu.Content>
            </Menu.Positioner>
          </Menu.Root>
        )}
      </Flex>
      <GroupedBarLegend series={barSeries} />
      <Chart.Root h={chartHeight} maxH={chartHeight} chart={chart}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={dataForChart} margin={{ top: 5, right: timescaleChartMarginRight(false), left: 20, bottom: 10 }} barCategoryGap="6%">
            <CartesianGrid stroke={chart.color("border")} strokeDasharray="3 3" vertical={false} />
            <XAxis
              axisLine={false}
              dataKey="timeMs"
              type="number"
              scale="time"
              domain={timeDomain ?? ["dataMin", "dataMax"]}
              stroke={chart.color("border")}
              fontSize={12}
              tickLine={false}
              tickFormatter={formatTimeTick}
              ticks={xAxisTicksMs}
              minTickGap={xAxisTicksMs?.length ? undefined : timeMinTickGap}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tickMargin={10}
              stroke={chart.color("border")}
              fontSize={12}
              tickFormatter={formatYAxisTick}
              allowDecimals={false}
              tickCount={6}
              // Level is a percentage, so the axis always spans at least 0 to 100
              domain={[0, (dataMax: number) => Math.max(100, niceYAxisMax(dataMax))]}
              label={
                graph?.yAxis
                  ? { value: graph.yAxis, angle: -90, position: "insideLeft" }
                  : undefined
              }
            />
            <Tooltip
              animationDuration={0}
              wrapperStyle={{ zIndex: 1000 }}
              content={<GroupedBarTooltip series={barSeries} unit={unit} />}
            />
            {barSeries.map((item) => (
              <Bar
                key={String(item.name)}
                isAnimationActive={false}
                dataKey={chart.key(item.name as never)}
                name={item.label}
                fill={chart.color(item.color)}
                maxBarSize={80}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Chart.Root>
    </Box>
  )
}
